/**
 * Orientation relief timing (LOCKED-DESIGN 18.15 / PLAYTEST F9).
 *
 * PLAYTEST F9 recorded the mechanism: `Tutorial.update()` only runs a countdown
 * for the `traverse` and `ready` beats. Simple's `clean` ("THROW IT IN!") and
 * `chase` ("FOLLOW THE RING!") advance ONLY through `reportIncinerated()`, which
 * needs a piece to physically reach the maw, and `chase` additionally needs the
 * scripted ring move. There is no timer, no attempt counter and no softening, so
 * a kid who cannot land a throw has no in-game path forward — the only exit is a
 * parent pressing skip. This module is the pure, testable rule that removes that
 * hard block.
 *
 * Two stages, deliberately separated so the game HELPS before it gives up on the
 * lesson:
 *   - SOFTEN at 12 s: friendlier sub-line, a wider aim assist and the big
 *     "THROW HERE" ring come back. The kid is still the one who clears the beat,
 *     so the lesson survives.
 *   - ADVANCE at 22 s: the beat releases on its own with a warm "nice try" —
 *     never a failure framing, never a silent skip.
 *
 * `chase` gates its 22 s release on `ringMoved` because graduating that beat
 * before the scripted ring move has fired would teach the wrong thing (the whole
 * point of the beat is seeing the ring move). ADVANCE_HARD_S is the backstop that
 * makes "a kid is never blocked" unconditional: at 35 s the beat releases even if
 * the ring move never fired, so no ordering bug anywhere else in the engine can
 * strand a child on an orientation card.
 *
 * Pure by design: no DOM, no engine, no clock. `Tutorial` accumulates the elapsed
 * time and applies the result; everything decision-shaped lives here so the six
 * cases can be unit-tested without a browser.
 */
import type { TutorialStepId } from "./Tutorial.js";

/** Seconds on a stuck beat before the game starts helping. */
export const SOFTEN_AFTER_S = 12;
/** Seconds before the beat releases on its own (gated on `ringMoved` for chase). */
export const ADVANCE_AFTER_S = 22;
/** Unconditional backstop — a kid is NEVER hard-blocked, ring move or not. */
export const ADVANCE_HARD_S = 35;

export interface OrientationRelief {
  /** Widen the assist, re-show the big throw ring, swap in the friendlier line. */
  soften: boolean;
  /** Release the beat with a warm toast (the caller decides how to announce it). */
  advance: boolean;
}

const NONE: OrientationRelief = { soften: false, advance: false };

/**
 * The relief state for a Simple orientation beat that has been on screen for
 * `elapsedSeconds`. Only `clean` and `chase` can ever return anything but
 * `{soften:false, advance:false}` — every other beat already has its own exit
 * (a countdown, a bulkhead hit, a portal placement) and is left untouched.
 */
export function orientationRelief(
  step: TutorialStepId,
  elapsedSeconds: number,
  ringMoved: boolean,
): OrientationRelief {
  if (step !== "clean" && step !== "chase") return NONE;
  const soften = elapsedSeconds >= SOFTEN_AFTER_S;
  if (step === "clean") return { soften, advance: elapsedSeconds >= ADVANCE_AFTER_S };
  // chase: the normal release waits for the scripted ring move, but the hard
  // backstop ignores it so an unfired ring move can never strand a kid.
  const advance = elapsedSeconds >= ADVANCE_HARD_S || (ringMoved && elapsedSeconds >= ADVANCE_AFTER_S);
  return { soften, advance };
}
